<template>
  <div class="competitor-price-ignore">
    <v-container fluid class="pa-0">
      <v-row v-if="loading" justify="center" class="mt-5">
        <v-progress-circular indeterminate color="primary" />
      </v-row>
      <v-row v-else-if="isDataEmpty" justify="center" class="mt-5">
        <v-col cols="6" class="text-center">
          <span class="subtitle-1 grey--text">
            {{ $tkey('emptyState') }}
          </span>
        </v-col>
      </v-row>
      <template v-else>
        <v-row class="mb-2" align="center">
          <v-col cols="2" class="ml-4 pr-2">
            <v-text-field
              v-model="searchString"
              flat
              dense
              hide-details
              class="inputs-actions__search"
              :label="$t('actions.search')"
              single-line
              append-icon="mdi-magnify"
              @input="debouncedFilterChange"
            />
          </v-col>
        </v-row>
        <ag-grid-vue
          :style="{ width: '100%', height: `${dataTableHeight}px` }"
          class="ag-theme-custom ag-theme-custom-stripped campaigns-grid"
          :grid-options="gridOptions"
          :column-defs="columnDefs"
          :row-data="rowData"
          @grid-ready="onGridReady"
          @modelUpdated="onModelUpdated"
        />
        <div class="campaigns-footer">
          <div class="campaigns-footer__meta">
            <div class="campaigns-footer__row-count">
              <span class="message">Rows: {{ displayedRowCount }}</span>
            </div>
            <div class="campaigns-footer__msg-container">
              <span v-if="notAllDataOnDisplay" class="message">{{
                $t('inputs.maxResultsShown', { limit: rowLimit })
              }}</span>
            </div>
          </div>
          <div class="campaigns-footer__btn-container">
            <v-btn
              class="grid-btn"
              secondary
              small
              :disabled="!hasPendingChanges || saving"
              depressed
              @click="discardChanges"
              >{{ $t('actions.cancel') }}</v-btn
            >
            <tooltip
              class="save-tooltip"
              :value="saveDisabledTooltipMessage"
              position="bottom"
              :disabled="!saveDisabledTooltipMessage"
            >
              <v-btn
                :disabled="!saveAllowed"
                :loading="saving"
                small
                color="primary"
                depressed
                @click="save"
              >
                {{ $t('actions.save') }}
                <v-icon small>$import</v-icon>
              </v-btn>
            </tooltip>
          </div>
        </div>
      </template>
    </v-container>

    <unsaved-changes-dialog
      v-if="showUnsavedChangesDialog"
      :show-dialog="showUnsavedChangesDialog"
      :save-allowed="saveAllowed"
      :save-not-allowed-message="saveDisabledUnsavedChangesDialogMessage"
      @closedUnsavedChangesDialog="handleUnsavedChangesDecision"
    />
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue';
import 'ag-grid-enterprise';
import axios from 'axios';
import moment from 'moment';
import { mapState, mapGetters, mapMutations } from 'vuex';
import { get, isNil, set, isEmpty, debounce, keyBy, forEach } from 'lodash';
import { displayCompetitorHistoricalPrices } from '@enums/feature-flags';
import { yearMonthDayFormat } from '@enums/date-formats';
import { save, cancel } from '@enums/inputs/unsaved-changes-options';
import calculateHeadersAndFootersHeight from '../../../utils/data-table-utils';
import buildColumnDefs from './competitor-price-ignore-column-defs';
import formatNumberMixin from '../../../mixins/formatNumber';
import formatLocalisedDateMixin from '../../../mixins/formatLocalisedDate';
import tkeyMixin from '../../../mixins/tkey';
import marginInitialisationMixin from '../../../mixins/marginInitialisation';
import datePickerEditor from '../../../components/date-picker-editor.vue';
import ignorePeriodCellRenderer from './ignore-period-cell-renderer.vue';
import unsavedChangesDialog from '../../../components/unsaved-changes-dialog.vue';

export default {
  name: 'CompetitorPriceIgnore',

  components: {
    AgGridVue,
    // eslint-disable-next-line vue/no-unused-components
    datePickerEditor,
    // eslint-disable-next-line vue/no-unused-components
    ignorePeriodCellRenderer,
    unsavedChangesDialog,
  },

  localizationKey: 'competitorPriceIgnore',

  mixins: [formatNumberMixin, formatLocalisedDateMixin, tkeyMixin, marginInitialisationMixin],

  async beforeRouteLeave(to, from, next) {
    const preventNavigation = await this.resolveEditsBeforeNavigation();
    if (preventNavigation) return;
    next();
  },

  data() {
    return {
      loading: false,
      saving: false,
      rowData: [],
      gridApi: null,
      dataTableHeight: 700,
      originalPriceIgnores: {},
      pendingChanges: {},
      showUnsavedChangesDialog: false,
      unsavedChangesModalOutcome: null,
      searchString: '',
      displayedRowCount: 0,
      pendingHistoryRows: null,
    };
  },

  computed: {
    ...mapState('workpackages', ['selectedWorkpackage']),
    ...mapState('clientConfig', [
      'i18nconfig',
      'inputsPages',
      'storeGroupCompetitorMatch',
      'toggleLogic',
    ]),
    ...mapState('storeGroupRelationships', ['storeGroupChains', 'storegroupKeyNameMap']),
    ...mapGetters('competitorMetadata', ['feedCompetitors']),

    workpackageId() {
      return get(this.selectedWorkpackage, '_id');
    },

    isDataEmpty() {
      return !this.loading && this.rowData.length === 0;
    },

    hasPendingChanges() {
      return !isEmpty(this.pendingChanges);
    },

    rowLimit() {
      return this.inputsPages.competitorPriceIgnoreTableRowsLimit;
    },

    notAllDataOnDisplay() {
      return this.rowLimit > 0 && this.rowData.length >= this.rowLimit;
    },

    columnDefs() {
      return buildColumnDefs(this);
    },

    gridOptions() {
      return {
        headerHeight: 30,
        rowHeight: 28,
        rowSelection: 'multiple',
        stopEditingWhenCellsLoseFocus: true,
        suppressMenuHide: true,
        tooltipShowDelay: 200,
        getRowId: params => String(params.data.productKey),
        suppressCopyRowsToClipboard: true,
        processCellForClipboard: params => this.processIgnorePeriodForClipboard(params),
        processCellFromClipboard: params => this.processIgnorePeriodFromClipboard(params),
        defaultColDef: {
          resizable: true,
          sortable: true,
          filter: true,
          menuTabs: ['filterMenuTab', 'generalMenuTab'],
        },
      };
    },

    saveAllowed() {
      const saveAllowed =
        this.hasPendingChanges && !this.saving && !this.runningMarginInitialisation;

      return saveAllowed;
    },

    saveDisabledTooltipMessage() {
      if (!this.hasPendingChanges) {
        return '';
      }
      if (this.saving) {
        return this.$t('tooltip.saveDisabledSavingInProgressMessage');
      }
      if (this.runningMarginInitialisation) {
        return this.$t('tooltip.saveDisabledMarginInitialisationRunningMessage');
      }
      return '';
    },

    saveDisabledUnsavedChangesDialogMessage() {
      const tooltipMessage = this.saveDisabledTooltipMessage;
      return tooltipMessage
        ? `${tooltipMessage} ${this.$t('unsavedChangesDialog.saveDisabledSuffix')}`
        : '';
    },
  },

  created() {
    if (this.workpackageId) {
      this.fetchData(this.workpackageId);
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.calculateDataTableHeight);
  },

  methods: {
    ...mapMutations('notifications', ['addNotification']),

    buildChangeKey(productKey, tsgKey, competitorKey) {
      return `${productKey}::${tsgKey}::${competitorKey}`;
    },

    buildIgnoreColumnIds(tsgKey, competitorKey) {
      return [
        `tsg::${tsgKey}::competitor::${competitorKey}::ignoreActive`,
        `tsg::${tsgKey}::competitor::${competitorKey}::ignorePeriod`,
      ];
    },

    hasPendingChange(productKey, tsgKey, competitorKey) {
      return !!this.pendingChanges[this.buildChangeKey(productKey, tsgKey, competitorKey)];
    },

    getAllIgnoreColumnIds() {
      if (!this.gridApi || !this.gridApi.getAllDisplayedColumns) {
        return [];
      }

      return this.gridApi
        .getAllDisplayedColumns()
        .map(column => column.getColId())
        .filter(colId => colId.endsWith('::ignoreActive') || colId.endsWith('::ignorePeriod'));
    },

    clearSelection() {
      if (this.gridApi && this.gridApi.deselectAll) {
        this.gridApi.deselectAll();
      }
    },

    async fetchData(workpackageId, { isBackground = false, clearPendingChanges = true } = {}) {
      if (!isBackground) {
        this.loading = true;
      }
      if (clearPendingChanges) {
        this.pendingChanges = {};
      }

      const showHistory = get(this.toggleLogic, displayCompetitorHistoricalPrices);
      // Fire both requests immediately so history fetches in parallel with base data.
      const historyRequest = showHistory
        ? axios.get(
            `/api/workpackage-product/workpackage/${workpackageId}/feed-competitor-1-12-weeks-history?weeks=3`
          )
        : null;

      try {
        const { data } = await axios.get(
          `/api/inputs/workpackage/${workpackageId}/competitor-price-ignore`
        );
        this.clearSelection();
        this.rowData = data;
        this.displayedRowCount = data.length;
        this.snapshotOriginalPriceIgnores();
        this.$nextTick(() => {
          this.calculateDataTableHeight();
        });

        if (historyRequest) {
          try {
            const { data: historyRows } = await historyRequest;
            this.mergeCompetitorHistory(historyRows);
          } catch {
            // non-critical — history columns will remain empty
          }
        }
      } catch {
        this.addNotification({
          id: `notifications.competitor_price_ignore_load-${Date.now()}`,
          jobStatus: 'failed',
          baseTranslation: 'notifications.competitor_price_ignore_load',
        });
      } finally {
        if (!isBackground) {
          this.loading = false;
        }
      }
    },

    mergeCompetitorHistory(historyRows) {
      if (!historyRows.length) return;
      if (!this.gridApi) {
        this.pendingHistoryRows = historyRows;
        return;
      }

      const historyByProductKey = keyBy(historyRows, 'productKey');
      const updates = [];

      this.gridApi.forEachNode(({ data }) => {
        const histRow = historyByProductKey[data.productKey];
        if (!histRow) return;

        const newData = { ...data };
        forEach(histRow.toolStoreGroups, (tsgHist, tsgKey) => {
          forEach(get(tsgHist, 'competitor.feed', {}), (feedHist, compKey) => {
            const feedPath = `toolStoreGroups.${tsgKey}.competitor.feed.${compKey}`;
            const existingFeed = get(newData, feedPath);
            if (existingFeed) {
              set(newData, feedPath, { ...existingFeed, ...feedHist });
            }
          });
        });
        updates.push(newData);
      });

      if (updates.length) {
        this.gridApi.applyTransaction({ update: updates });
      }
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.$nextTick(() => {
        this.calculateDataTableHeight();
        window.addEventListener('resize', this.calculateDataTableHeight);
      });
      this.applyDefaultSort();
      if (this.pendingHistoryRows) {
        this.mergeCompetitorHistory(this.pendingHistoryRows);
        this.pendingHistoryRows = null;
      }
    },

    onModelUpdated() {
      if (!this.gridApi || !this.gridApi.getDisplayedRowCount) {
        return;
      }

      this.displayedRowCount = this.gridApi.getDisplayedRowCount();
    },

    applyDefaultSort() {
      if (!this.gridApi || this.feedCompetitors.length === 0) return;
      const tsgKeys = Object.keys(this.storegroupKeyNameMap || {});
      if (!tsgKeys.length) return;
      // Default sort: largest competitor reference-date delta descending, first TSG
      const firstTsgKey = tsgKeys[0];
      const firstCompetitor = this.feedCompetitors[0];
      const deltaColId = `tsg::${firstTsgKey}::competitor::${firstCompetitor.competitorKey}::delta`;
      this.gridApi.applyColumnState({
        state: [{ colId: deltaColId, sort: 'desc' }],
        defaultState: { sort: null },
      });
    },

    getTargetNodesForBulkEdit(anchorNode, tsgKey, competitorKey) {
      if (!anchorNode) return [];

      const selectedNodes =
        this.gridApi && this.gridApi.getSelectedNodes ? this.gridApi.getSelectedNodes() : [];
      const anchorProductKey = get(anchorNode, 'data.productKey');
      const useSelectedNodes =
        selectedNodes.length > 1 &&
        selectedNodes.some(node => get(node, 'data.productKey') === anchorProductKey);
      const candidateNodes = useSelectedNodes ? selectedNodes : [anchorNode];

      return candidateNodes.filter(
        node =>
          !!get(
            node,
            `data.toolStoreGroups.${tsgKey}.competitor.feed.${competitorKey}.competitorKey`
          )
      );
    },

    applyPriceIgnoreToNodes(nodes, tsgKey, competitorKey, effectiveDate, expiryDate) {
      if (nodes.length === 0) {
        return false;
      }

      const priceIgnorePath = `toolStoreGroups.${tsgKey}.competitor.feed.${competitorKey}.priceIgnore`;
      const priceIgnore = effectiveDate && expiryDate ? { effectiveDate, expiryDate } : null;

      const updates = nodes.map(node => {
        const newData = { ...node.data };
        set(newData, priceIgnorePath, priceIgnore);
        this.trackCampaignChange(
          node.data.productKey,
          tsgKey,
          competitorKey,
          effectiveDate,
          expiryDate
        );
        return newData;
      });

      this.gridApi.applyTransaction({ update: updates });
      return true;
    },

    refreshIgnoreCells(rowNodes = [], tsgKey = null, competitorKey = null) {
      if (!this.gridApi) {
        return;
      }

      const columns =
        !isNil(tsgKey) && !isNil(competitorKey)
          ? this.buildIgnoreColumnIds(tsgKey, competitorKey)
          : this.getAllIgnoreColumnIds();

      if (columns.length === 0) {
        return;
      }

      const refreshParams = {
        columns,
        force: true,
      };

      if (rowNodes.length > 0) {
        refreshParams.rowNodes = rowNodes;
      }

      this.gridApi.refreshCells(refreshParams);
    },

    parseIgnorePeriodColId(colId) {
      const match = colId.match(/^tsg::(.+?)::competitor::(.+?)::ignorePeriod$/);
      if (!match) return null;
      return { tsgKey: match[1], competitorKey: match[2] };
    },

    processIgnorePeriodForClipboard(params) {
      const colId = params.column.getColId();
      const parsed = this.parseIgnorePeriodColId(colId);
      if (!parsed) return params.value;

      const priceIgnore = get(
        params.node,
        `data.toolStoreGroups.${parsed.tsgKey}.competitor.feed.${parsed.competitorKey}.priceIgnore`
      );

      if (!priceIgnore || !priceIgnore.effectiveDate || !priceIgnore.expiryDate) return '';
      return `${priceIgnore.effectiveDate}|${priceIgnore.expiryDate}`;
    },

    processIgnorePeriodFromClipboard(params) {
      const colId = params.column.getColId();
      if (!this.parseIgnorePeriodColId(colId)) return params.value;

      const value = params.value;
      if (!value || !value.includes('|')) return null;

      const [start, end] = value.split('|');
      if (!start || !end) return null;

      return { start, end, dateHasChanged: true };
    },

    getGoLivePlusOneDate() {
      const goLiveDate = get(this.selectedWorkpackage, 'goLiveDate');

      if (!goLiveDate) {
        return null;
      }

      return moment(goLiveDate)
        .add(1, 'day')
        .format(yearMonthDayFormat);
    },

    getDefaultIgnoreDates() {
      const effectiveDate = moment().format(yearMonthDayFormat);
      const goLivePlusOne = this.getGoLivePlusOneDate();
      const expiryDate =
        goLivePlusOne && effectiveDate <= goLivePlusOne ? goLivePlusOne : effectiveDate;

      return { effectiveDate, expiryDate };
    },

    shouldApplyIgnorePeriod(nodes, tsgKey, competitorKey, start, end, dateHasChanged) {
      if (dateHasChanged) {
        return true;
      }

      return nodes.some(node => {
        const priceIgnore = get(
          node,
          `data.toolStoreGroups.${tsgKey}.competitor.feed.${competitorKey}.priceIgnore`
        );

        return (
          get(priceIgnore, 'effectiveDate', null) !== start ||
          get(priceIgnore, 'expiryDate', null) !== end
        );
      });
    },

    getEditableCellClasses(params, tsgKey, competitorKey, extraClasses = []) {
      const classes = ['campaign-editable-cell', ...extraClasses];

      if (this.hasPendingChange(get(params, 'data.productKey'), tsgKey, competitorKey)) {
        classes.push('campaign-edited-cell');
      }

      return classes;
    },

    getCampaignDatePickerParams(params, tsgKey, competitorKey) {
      const priceIgnorePath = `toolStoreGroups.${tsgKey}.competitor.feed.${competitorKey}.priceIgnore`;
      const priceIgnore = get(params.data, priceIgnorePath) || {};
      const hasExistingDates = priceIgnore.effectiveDate || priceIgnore.expiryDate;

      const goLiveDate = get(this.selectedWorkpackage, 'goLiveDate');

      let value;
      if (hasExistingDates) {
        value = [priceIgnore.effectiveDate, priceIgnore.expiryDate].filter(Boolean);
      } else if (goLiveDate) {
        const { effectiveDate, expiryDate } = this.getDefaultIgnoreDates();
        value = [effectiveDate, expiryDate];
      } else {
        value = [];
      }

      return {
        range: true,
        minDate: () => undefined,
        firstDayOfWeek: this.i18nconfig.firstDayOfTheWeek,
        locale: this.i18nconfig.fallbackLocale,
        autoSelectToday: !hasExistingDates && !goLiveDate,
        value,
        actions:
          value.length > 0
            ? [
                {
                  label: this.$tkey('clearPriceIgnore'),
                  callback: () => {
                    const targetNodes = this.getTargetNodesForBulkEdit(
                      params.node,
                      tsgKey,
                      competitorKey
                    );
                    this.applyPriceIgnoreToNodes(targetNodes, tsgKey, competitorKey, null, null);
                    params.api.stopEditing(true);
                  },
                },
              ]
            : [],
      };
    },

    handleCampaignToggle(params, tsgKey, competitorKey, isActive) {
      const targetNodes = this.getTargetNodesForBulkEdit(params.node, tsgKey, competitorKey);
      if (targetNodes.length === 0) {
        return;
      }

      if (isActive) {
        const { effectiveDate, expiryDate } = this.getDefaultIgnoreDates();
        this.applyPriceIgnoreToNodes(targetNodes, tsgKey, competitorKey, effectiveDate, expiryDate);
      } else {
        this.applyPriceIgnoreToNodes(targetNodes, tsgKey, competitorKey, null, null);
      }
    },

    handleCampaignToggleValueSetter(params, tsgKey, competitorKey) {
      if (isNil(params.newValue) || params.newValue === params.oldValue) {
        return false;
      }

      this.handleCampaignToggle(params, tsgKey, competitorKey, params.newValue);
      return true;
    },

    handleCampaignDateValueSetter(params, tsgKey, competitorKey) {
      const newValue = params.newValue;

      if (!newValue || !newValue.start || !newValue.end) {
        return false;
      }

      const targetNodes = this.getTargetNodesForBulkEdit(params.node, tsgKey, competitorKey);

      if (
        !this.shouldApplyIgnorePeriod(
          targetNodes,
          tsgKey,
          competitorKey,
          newValue.start,
          newValue.end,
          newValue.dateHasChanged
        )
      ) {
        return false;
      }

      this.applyPriceIgnoreToNodes(
        targetNodes,
        tsgKey,
        competitorKey,
        newValue.start,
        newValue.end
      );

      return true;
    },

    trackCampaignChange(productKey, tsgKey, competitorKey, effectiveDate, expiryDate) {
      const changeKey = this.buildChangeKey(productKey, tsgKey, competitorKey);
      const original = this.originalPriceIgnores[changeKey] || null;
      const originalEffectiveDate = original ? original.effectiveDate : null;
      const originalExpiryDate = original ? original.expiryDate : null;

      if (effectiveDate === originalEffectiveDate && expiryDate === originalExpiryDate) {
        this.$delete(this.pendingChanges, changeKey);
      } else {
        this.$set(this.pendingChanges, changeKey, {
          productKey,
          toolStoreGroupKey: tsgKey,
          competitorKey,
          effectiveDate,
          expiryDate,
        });
      }
    },

    /**
     * Snapshots the original price-ignore state for each product/TSG/competitor combination
     * from the loaded row data, so we can detect when changes revert to the original.
     */
    snapshotOriginalPriceIgnores() {
      const snapshot = {};
      this.rowData.forEach(row => {
        const tsgGroups = row.toolStoreGroups || {};
        Object.keys(tsgGroups).forEach(tsgKey => {
          const tsg = tsgGroups[tsgKey];
          const feedCompetitors =
            tsg && tsg.competitor && tsg.competitor.feed ? tsg.competitor.feed : {};
          Object.keys(feedCompetitors).forEach(competitorKey => {
            const changeKey = `${row.productKey}::${tsgKey}::${competitorKey}`;
            const comp = feedCompetitors[competitorKey];
            const priceIgnore = comp ? comp.priceIgnore : null;
            snapshot[changeKey] = priceIgnore
              ? {
                  effectiveDate: priceIgnore.effectiveDate || null,
                  expiryDate: priceIgnore.expiryDate || null,
                }
              : null;
          });
        });
      });
      this.originalPriceIgnores = snapshot;
    },

    /**
     * Restores price-ignore values in the current grid data from the original snapshot,
     * so Cancel updates UI immediately before the background refetch completes.
     */
    restoreOriginalPriceIgnoresToRowData() {
      if (!this.gridApi) return;

      const updates = [];
      this.gridApi.forEachNode(({ data }) => {
        const newData = { ...data };
        const tsgGroups = data.toolStoreGroups || {};
        Object.keys(tsgGroups).forEach(tsgKey => {
          const tsg = tsgGroups[tsgKey];
          const feedCompetitors = get(tsg, 'competitor.feed', {});
          Object.keys(feedCompetitors).forEach(competitorKey => {
            const changeKey = `${data.productKey}::${tsgKey}::${competitorKey}`;
            const priceIgnorePath = `toolStoreGroups.${tsgKey}.competitor.feed.${competitorKey}.priceIgnore`;
            set(newData, priceIgnorePath, this.originalPriceIgnores[changeKey] || null);
          });
        });
        updates.push(newData);
      });

      if (updates.length) {
        this.gridApi.applyTransaction({ update: updates });
      }
    },

    discardChanges() {
      this.pendingChanges = {};
      this.clearSelection();
      this.restoreOriginalPriceIgnoresToRowData();
      if (this.workpackageId) {
        this.fetchData(this.workpackageId, {
          isBackground: true,
          clearPendingChanges: false,
        });
      }
    },

    async save() {
      const updates = Object.values(this.pendingChanges);
      if (isEmpty(updates)) return;
      if (!this.workpackageId) return;

      this.saving = true;
      try {
        await axios.post(`/api/inputs/workpackage/${this.workpackageId}/competitor-price-ignore`, {
          updates,
        });
        this.pendingChanges = {};
        this.clearSelection();
        this.snapshotOriginalPriceIgnores();
        this.refreshIgnoreCells();
        this.runMarginInitialisation();
        this.addNotification({
          id: `competitor_price_ignore_save-${Date.now()}`,
          jobStatus: 'finished',
          baseTranslation: 'notifications.competitor_price_ignore_save',
        });
      } catch {
        this.addNotification({
          id: `notifications.competitor_price_ignore_save-${Date.now()}`,
          jobStatus: 'failed',
          baseTranslation: 'notifications.competitor_price_ignore_save',
        });
      } finally {
        this.saving = false;
      }
    },

    calculateDataTableHeight() {
      this.dataTableHeight =
        window.innerHeight -
        calculateHeadersAndFootersHeight('.campaigns-grid', this.dataTableHeight) -
        1;
    },

    async resolveEditsBeforeNavigation() {
      const preventNavigation = false;
      if (this.hasPendingChanges) {
        this.showUnsavedChangesDialog = true;
        // The promise here stays pending until the modal assigns a value, then continues.
        const userChoice = await new Promise(resolve => {
          this.unsavedChangesModalOutcome = resolve;
        });

        // Discard is handled by the default case
        // If the navigation proceeds, state is discarded automatically
        if (userChoice === cancel) {
          this.showUnsavedChangesDialog = false;
          return !preventNavigation;
        }
        if (userChoice === save) {
          await this.save();
        }
      }

      this.showUnsavedChangesDialog = false;
      return preventNavigation;
    },

    handleUnsavedChangesDecision(action) {
      if (this.unsavedChangesModalOutcome) {
        this.unsavedChangesModalOutcome(action);
      }
    },
    // Debounced handler for search field input — applies AG Grid quick filter.
    debouncedFilterChange: debounce(function(newFilter) {
      this.gridApi.setQuickFilter(newFilter);
    }, 800),
  },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';

.competitor-price-ignore {
  width: 100%;
  height: 100%;
}

::v-deep .ag-row {
  border-bottom: none;
  border-top: none;
}

::v-deep .campaigns-grid.ag-theme-custom .ag-row.ag-row-selected {
  background-color: $grid-view-selected-row !important;
  box-shadow: inset 3px 0 0 $custom-ag-grid-primary-color;
}

::v-deep .campaigns-grid.ag-theme-custom .ag-row.ag-row-selected:hover {
  background-color: mix($grid-view-hover-color, $grid-view-selected-row, 35%) !important;
}

::v-deep .ag-header-cell-resize::after {
  display: none !important;
}

::v-deep .border-right {
  border-right: 1px solid $custom-ag-grid-header-separator-color !important;
}

::v-deep .ag-cell.campaign-editable-cell {
  cursor: pointer;
}

::v-deep .ag-cell.campaign-edited-cell {
  background-color: $ag-grid-edit-background-color;
}

::v-deep .ag-cell.campaign-toggle-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

::v-deep .ag-cell.campaign-toggle-cell.ag-cell-inline-editing {
  display: flex;
  align-items: center;
  justify-content: center;
}

::v-deep .ag-cell.campaign-toggle-cell.ag-cell-inline-editing .ag-cell-edit-wrapper {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.campaigns-footer {
  height: 50px;
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: white;
  gap: 16px;
  border-bottom: 1px solid $grid-header-footer-border-color;

  &__meta {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__btn-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
}

.save-tooltip ::v-deep .tooltip-container {
  right: 0;
  font-size: 1.25rem;
}
</style>
